import { useEffect } from "react";
import { StyleSheet, Text, View } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Svg, { Path } from "react-native-svg";
import { colors } from "./colors";
import { EASE, MOTION } from "./motion";

/**
 * "Tickets destination" and the address the tickets go to, as the web
 * checkout draws it in host mode (web/destination.tsx): a chip with the
 * verified tick. No ×: in the app the address is the fan's own sign-in,
 * which the checkout cannot change.
 */
export function DestinationChip({
  email,
  verified,
  label = "Tickets destination",
}: {
  email: string;
  verified: boolean;
  /** What the address is for: the tickets' unless the cart sells
   *  something else (`CHECKOUT_WORDS`). */
  label?: string;
}) {
  const ticked = useSharedValue(verified ? 1 : 0);
  useEffect(() => {
    ticked.value = withTiming(verified ? 1 : 0, {
      duration: MOTION.durationMs,
      easing: EASE.motion,
    });
  }, [verified, ticked]);
  // The tick fills in green once verified, on the checkout's one motion.
  const tick = useAnimatedStyle(() => ({
    opacity: 0.5 + 0.5 * ticked.value,
    transform: [{ scale: 0.85 + 0.15 * ticked.value }],
  }));
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.chip} testID="checkout-destination">
        <Animated.View
          accessibilityLabel={verified ? "Verified" : undefined}
          style={[styles.tick, verified ? styles.ticked : null, tick]}
        >
          {verified ? (
            <Svg width={10} height={10} viewBox="0 0 10 10">
              <Path
                d="M2 5.2 4.1 7.2 8 3"
                fill="none"
                stroke="#fff"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          ) : null}
        </Animated.View>
        <Text numberOfLines={1} ellipsizeMode="middle" style={styles.email}>
          {email}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
  },
  label: { color: colors.text, fontSize: 13, fontWeight: "600" },
  chip: {
    alignItems: "center",
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: "row",
    flexShrink: 1,
    gap: 6,
    // The web chip's height, its × included, so the row does not change.
    minHeight: 26,
    minWidth: 0,
    paddingLeft: 6,
    paddingRight: 10,
    paddingVertical: 3,
  },
  tick: {
    alignItems: "center",
    borderColor: colors.secondary,
    borderRadius: 999,
    borderWidth: 1.5,
    height: 16,
    justifyContent: "center",
    width: 16,
  },
  ticked: { backgroundColor: colors.success, borderWidth: 0 },
  email: { color: colors.text, flexShrink: 1, fontSize: 13 },
});
